import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { UserProvider, useUser } from '@/context/UserContext';
import { useAuth } from '@/auth/useAuth';
import { canAccessAdmin, canAccessSubmissions, canAccessConsortium, canAccessIntegrations, canAccessTraining, canAccessPeerReviews, canAccessAnalytics, canAccessPeerMarkAdmin, canAccessRubrics } from '@/auth/roles';
import Layout from '@/components/Layout';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import AuthCallback from '@/pages/AuthCallback';
import LtiCallback from '@/pages/LtiCallback';
import LtiLaunch from '@/pages/LtiLaunch';

// Lazy-loaded app pages (code splitting)
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Submissions = lazy(() => import('@/pages/Submissions'));
const Reports = lazy(() => import('@/pages/Reports'));
const PeerReviews = lazy(() => import('@/pages/PeerReviews'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const PeerMarkAdmin = lazy(() => import('@/pages/PeerMarkAdmin'));
const RubricsPage = lazy(() => import('@/pages/RubricsPage'));
const Settings = lazy(() => import('@/pages/Settings'));
const Consortium = lazy(() => import('@/pages/Consortium'));
const Integrations = lazy(() => import('@/pages/Integrations'));
const Training = lazy(() => import('@/pages/Training'));
const Admin = lazy(() => import('@/pages/Admin'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Platform admin route — redirect others to dashboard */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessAdmin(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Submissions: report_reader has no access */
function SubmissionsRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessSubmissions(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Consortium: report_reader has no access */
function ConsortiumRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessConsortium(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Integrations: admin and reviewer only */
function IntegrationsRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessIntegrations(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Training: admin, reviewer, reader (report_reader has no access) */
function TrainingRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessTraining(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Peer reviews: any authenticated role (allocations enforce access) */
function PeerReviewsRoute({ children }: { children: React.ReactNode }) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!canAccessPeerReviews(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/** Analytics / PeerMark admin / Rubrics — platform + tenant admin only */
function WorkspaceAdminRoute({
  children,
  allow,
}: {
  children: React.ReactNode;
  allow: (role: ReturnType<typeof useUser>['role']) => boolean;
}) {
  const { role, loading } = useUser();
  if (loading) return <PageLoader />;
  if (!allow(role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/lti/launch" element={<LtiLaunch />} />
        <Route path="/lti/callback" element={<LtiCallback />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="submissions" element={<SubmissionsRoute><Submissions /></SubmissionsRoute>} />
          <Route path="reports" element={<Reports />} />
          <Route
            path="analytics"
            element={
              <WorkspaceAdminRoute allow={canAccessAnalytics}>
                <Analytics />
              </WorkspaceAdminRoute>
            }
          />
          <Route
            path="peermark"
            element={
              <WorkspaceAdminRoute allow={canAccessPeerMarkAdmin}>
                <PeerMarkAdmin />
              </WorkspaceAdminRoute>
            }
          />
          <Route
            path="rubrics"
            element={
              <WorkspaceAdminRoute allow={canAccessRubrics}>
                <RubricsPage />
              </WorkspaceAdminRoute>
            }
          />
          <Route path="peer-reviews" element={<PeerReviewsRoute><PeerReviews /></PeerReviewsRoute>} />
          <Route path="support" element={<Navigate to="/dashboard" replace />} />
          <Route path="training" element={<TrainingRoute><Training /></TrainingRoute>} />
          <Route path="settings" element={<Settings />} />
          <Route path="consortium" element={<ConsortiumRoute><Consortium /></ConsortiumRoute>} />
          <Route path="integrations" element={<IntegrationsRoute><Integrations /></IntegrationsRoute>} />
          <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <UserProvider>
      <AppRoutes />
    </UserProvider>
  );
}

function PageLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg)' }}>
      <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
    </div>
  );
}

export default App;
